import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { EmptyState } from "../../components/EmptyState";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime, formatNumber, normalizeText } from "../../lib/format";

type ArchiveCheck = {
  key: string;
  label: string;
  ok: boolean;
  action: string;
};

type ArchivePost = {
  id: string;
  title: string;
  slug: string;
  category: string;
  publishedAt: string;
  updatedAt: string;
  heroImageUrl: string;
  heroImageAlt: string;
  galleryCount: number;
  bodyBlockCount: number;
  checks: ArchiveCheck[];
  issueCount: number;
  readyForCafe24: boolean;
  sanityEditUrl: string;
  cafe24PreviewUrl: string;
};

type ArchiveProject = {
  projectId: string;
  dataset: string;
  studioUrl: string;
  manageUrl: string;
};

const fallbackProject: ArchiveProject = {
  projectId: "83em9rws",
  dataset: "production",
  studioUrl: "https://glasshalf-archive.sanity.studio/",
  manageUrl: "https://sanity.io/organizations/oKaNT0PTV/project/83em9rws"
};

export function ArchivePage() {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const archiveQuery = useQuery({ queryKey: ["archive-posts"], queryFn: api.archivePosts });
  const payload = archivePayload(archiveQuery.data);
  const posts = payload.posts;
  const project = payload.project;
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const text = normalizeText([post.title, post.slug, post.category].join(" "));
      if (query && !text.includes(normalizeText(query))) return false;
      return true;
    });
  }, [posts, query]);
  const columns = useMemo<ColumnDef<ArchivePost>[]>(() => [
    {
      accessorKey: "title",
      header: "Archive 글",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.title}</strong>
          <span>{row.original.slug ? `/archive/${row.original.slug}` : "slug 확인 필요"}</span>
        </div>
      ),
      size: 280
    },
    { accessorKey: "category", header: "카테고리", cell: ({ row }) => row.original.category || "-" },
    { accessorKey: "publishedAt", header: "발행일", cell: ({ row }) => formatDateTime(row.original.publishedAt) },
    {
      id: "content",
      header: "콘텐츠",
      cell: ({ row }) => `${formatNumber(row.original.galleryCount)} 이미지 · ${formatNumber(row.original.bodyBlockCount)} 본문`
    },
    {
      id: "image",
      header: "대표 이미지",
      cell: ({ row }) => row.original.heroImageUrl ? "있음" : "확인 필요"
    },
    {
      accessorKey: "issueCount",
      header: "상태",
      cell: ({ row }) => row.original.issueCount > 0
        ? <StatusBadge tone="warning">{formatNumber(row.original.issueCount)}개 확인</StatusBadge>
        : <StatusBadge tone="success">표시 가능</StatusBadge>
    }
  ], []);

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Archive 콘텐츠 목록</h2>
          </div>
          <StatusBadge tone={archiveQuery.isFetching ? "info" : payload.ok ? "success" : "warning"}>
            {archiveQuery.isFetching ? "읽는 중" : payload.ok ? "Sanity 읽기 완료" : "Sanity 확인 필요"}
          </StatusBadge>
        </div>
        <div className="archive-list-toolbar">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="제목, slug, 카테고리" />
          </label>
          <div className="meta-strip">
            <span>전체</span>
            <strong>{formatNumber(payload.summary.totalCount)}개</strong>
          </div>
        </div>
        {payload.ok ? (
          <DataTable
            columns={columns}
            data={filteredPosts}
            getRowId={(row) => row.id || row.slug}
            selectedId={selectedId}
            onSelect={(post) => setSelectedId((current) => current === post.id ? "" : post.id)}
            renderExpandedRow={(post) => <ArchivePostExpanded post={post} />}
            emptyTitle="조건에 맞는 Archive 글이 없습니다."
          />
        ) : (
          <EmptyState title="Sanity 공개 데이터를 읽지 못했습니다." body={payload.message || "잠시 후 다시 확인해 주세요."} />
        )}
      </section>

      <ArchiveUtilityBar project={project} connected={payload.ok} queryUrl={payload.queryUrl} />
    </div>
  );
}

function ArchiveUtilityBar({ project, connected, queryUrl }: { project: ArchiveProject; connected: boolean; queryUrl: string }) {
  return (
    <section className="panel archive-utility-bar">
      <div>
        <h2>Archive 도구</h2>
        <p>
          <StatusBadge tone={connected ? "success" : "warning"}>{connected ? "Sanity 읽기 가능" : "Sanity 확인 필요"}</StatusBadge>
          <span>Sanity · {project.dataset}</span>
        </p>
      </div>
      <div className="archive-utility-links">
        <ExternalAction href={project.studioUrl} title="Archive Studio" body="글 작성" />
        <ExternalAction href={project.manageUrl} title="Sanity 설정" body="프로젝트 관리" />
        {queryUrl ? <ExternalAction href={queryUrl} title="Published JSON" body="공개 데이터" /> : null}
        <Link to="/settings?view=audit">감사 로그<span>발행/동기화 이력</span></Link>
      </div>
    </section>
  );
}

function ArchivePostExpanded({ post }: { post: ArchivePost }) {
  const failedChecks = post.checks.filter((check) => !check.ok);
  const hasIssues = failedChecks.length > 0;
  return (
    <div className={`archive-expanded${hasIssues ? "" : " is-preview-only"}`}>
      {hasIssues ? (
        <div className="archive-check-panel">
          <div>
            <h3>확인 필요</h3>
            <p>{formatNumber(failedChecks.length)}개 항목을 확인해야 합니다.</p>
          </div>
          <div className="archive-check-list">
            {failedChecks.map((check) => (
              <div className="archive-check-row" key={check.key}>
                <span>{check.label}</span>
                <strong>{check.action}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="archive-preview-panel">
        <div>
          <h3>Cafe24 표시 확인</h3>
        </div>
        {post.heroImageUrl ? <img src={post.heroImageUrl} alt={post.heroImageAlt || post.title} /> : <div className="archive-image-empty">대표 이미지 없음</div>}
        <div className="archive-link-list">
          <ExternalAction href={post.sanityEditUrl} title="Sanity에서 수정" body={post.title} />
          <ExternalAction href={post.cafe24PreviewUrl} title="Cafe24 미리보기" body={post.slug || "slug 확인 필요"} />
        </div>
      </div>
    </div>
  );
}

function ExternalAction({ href, title, body }: { href: string; title: string; body: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      <span className="archive-action-title">
        {title}
        <ExternalLink size={13} aria-hidden="true" />
      </span>
      <span>{body}</span>
    </a>
  );
}

function archivePayload(value: unknown): {
  ok: boolean;
  message: string;
  project: ArchiveProject;
  queryUrl: string;
  summary: { totalCount: number; readyCount: number; needsReviewCount: number };
  posts: ArchivePost[];
} {
  const record = asRecord(value);
  const project = { ...fallbackProject, ...asRecord(record.project) };
  const posts = Array.isArray(record.posts) ? record.posts.map(archivePost).filter(Boolean) as ArchivePost[] : [];
  return {
    ok: record.ok !== false,
    message: String(record.message || ""),
    project,
    queryUrl: String(record.queryUrl || ""),
    summary: {
      totalCount: Number(asRecord(record.summary).totalCount || posts.length),
      readyCount: Number(asRecord(record.summary).readyCount || posts.filter((post) => post.readyForCafe24).length),
      needsReviewCount: Number(asRecord(record.summary).needsReviewCount || posts.filter((post) => post.issueCount > 0).length)
    },
    posts
  };
}

function archivePost(value: unknown): ArchivePost {
  const record = asRecord(value);
  const checks = Array.isArray(record.checks) ? record.checks.map((check) => {
    const item = asRecord(check);
    return {
      key: String(item.key || ""),
      label: String(item.label || ""),
      ok: Boolean(item.ok),
      action: String(item.action || "확인")
    };
  }) : [];
  return {
    id: String(record.id || record.slug || ""),
    title: String(record.title || "(제목 없음)"),
    slug: String(record.slug || ""),
    category: String(record.category || ""),
    publishedAt: String(record.publishedAt || ""),
    updatedAt: String(record.updatedAt || ""),
    heroImageUrl: String(record.heroImageUrl || ""),
    heroImageAlt: String(record.heroImageAlt || ""),
    galleryCount: Number(record.galleryCount || 0),
    bodyBlockCount: Number(record.bodyBlockCount || 0),
    checks,
    issueCount: Number(record.issueCount || checks.filter((check) => !check.ok).length),
    readyForCafe24: Boolean(record.readyForCafe24),
    sanityEditUrl: String(record.sanityEditUrl || fallbackProject.studioUrl),
    cafe24PreviewUrl: String(record.cafe24PreviewUrl || "https://glasshalf.co/page/archive.html")
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
